import { createContext, useContext, useMemo } from 'react';
import { jsx } from 'react/jsx-runtime';

const FeatureFlaggingContext = /*#__PURE__*/ createContext(null);
const useFeatureFlaggingContext = ()=>{
    const context = useContext(FeatureFlaggingContext);
    if (context === null) {
        throw new Error("useFeatureFlaggingContext must be used within a FeatureFlaggingProvider");
    }
    return context;
};

function useFeatureFlagging() {
    const context = useFeatureFlaggingContext();
    return {
        /**
		 * Get available feature flags.
		 * @returns All feature flags available in the context or undefined.
		 * Possible reasons for undefined value:
		 * - no visitorCode available;
		 * - no consent provided;
		 * - no feature flags set for this brand/environment;
		 * - Kameleoon initization failure.
		 */ getFeatureFlags: ()=>context.featureFlags,
        /**
		 * Retrieves the value of a feature flag from its key.
		 * @param key The feature flag key.
		 * @returns The feature flag value or undefined.
		 * Possible reasons for undefined value:
		 * - no visitorCode available;
		 * - no consent provided;
		 * - no feature flags set for this brand/environment;
		 * - Kameleoon initization failure.
		 */ getFeatureFlag: (key)=>context.featureFlags?.[key],
        /**
		 * @deprecated use `getFeatureFlag` instead.
		 */ getVariation: (key)=>context.featureFlags?.[key],
        /**
		 * @returns True if the client is initialized AND FeatureFlags are available.
		 */ isInitialized: Boolean(context.featureFlags),
        /**
		 * @deprecated use `isInitialized` instead.
		 */ isLoaded: Boolean(context.featureFlags)
    };
}

const FeatureFlaggingProvider = ({ children, strategy, hasUserConsent })=>{
    const { useData } = strategy;
    const { featureFlags } = useData(hasUserConsent);
    const contextValue = useMemo(()=>({
            featureFlags
        }), [
        featureFlags
    ]);
    return /*#__PURE__*/ jsx(FeatureFlaggingContext.Provider, {
        value: contextValue,
        children: children
    });
};

export { FeatureFlaggingProvider, useFeatureFlagging };
